import { useEffect, useRef, useState } from 'react'

import {
  EMPTY_FUNCTION,
  EMPTY_OBJECT,
  EMPTY_STRING,
} from '../../constants/common'
import {
  SpeechToTextError,
  SpeechToTextStatus,
} from '../../constants/speechToText'
import { isValueStringType } from '../../utils/common'
import {
  isWebSocketNormalCloseCode,
  mapDeepgramCloseToError,
  useDeepgramWebSocket,
} from '../useDeepgramWebSocket'
import { useInitData } from '../useInitData'
import {
  DEFAULT_CHUNK_MS,
  DEFAULT_MIME_TYPE,
  isMediaRecorderSupported,
  isMicrophonePermissionStateDenied,
  probeMicrophonePermission,
  useMicrophoneStream,
} from '../useMicrophoneStream'

import {
  DEEPGRAM_LIVE_QUERY,
  DEEPGRAM_LIVE_URL,
  DEEPGRAM_WEB_SOCKET_ERROR_TO_SPEECH_TO_TEXT_ERROR,
  DeepgramMessageType,
  MICROPHONE_STREAM_ERROR_TO_SPEECH_TO_TEXT_ERROR,
  SPEECH_TO_TEXT_DRAIN_TIMEOUT_MS,
} from './useSpeechToText.constants'
import {
  isDeepgramMessageTypeResults,
  isSpeechToTextStatusIdle,
  isSpeechToTextStatusReady,
  isSpeechToTextStatusRecording,
  isSpeechToTextStatusStopping,
} from './useSpeechToText.utils'

export function useSpeechToText({
  drainTimeoutMs = SPEECH_TO_TEXT_DRAIN_TIMEOUT_MS,
  liveQuery = DEEPGRAM_LIVE_QUERY,
  mimeType = DEFAULT_MIME_TYPE,
  recorderChunkMs = DEFAULT_CHUNK_MS,
  onError = EMPTY_FUNCTION,
  onFinalTranscript = EMPTY_FUNCTION,
  onInterimTranscript = EMPTY_FUNCTION,
  onMicrophoneReady = EMPTY_FUNCTION,
  onStatusChange = EMPTY_FUNCTION,
} = EMPTY_OBJECT) {
  const { deepgram_api_key: apiKey } = useInitData()

  const [status, setStatusState] = useState(SpeechToTextStatus.Idle)
  const [errorCode, setErrorCode] = useState(null)
  const [finalTranscript, setFinalTranscript] = useState(EMPTY_STRING)
  const [interimTranscript, setInterimTranscript] = useState(EMPTY_STRING)

  const callbacksRef = useRef({
    onError,
    onFinalTranscript,
    onInterimTranscript,
    onMicrophoneReady,
    onStatusChange,
  })
  const statusRef = useRef(status)
  const isMountedRef = useRef(true)

  // Latest-ref sync (intentionally no deps). Consumer callbacks are typically
  // inline, so their identities change every render — listing them as deps
  // would re-run this effect every render anyway. Omitting the array makes the
  // "sync on every commit" intent explicit. The sync lives in an effect (not
  // in render) so it only fires after a committed render, which is safe under
  // concurrent rendering. Reading these through the ref lets the internal
  // callbacks below stay referentially stable, so the WS/mic sub-hooks don't
  // tear down and re-subscribe their handlers on every parent re-render.
  useEffect(() => {
    const callbacks = callbacksRef.current

    callbacks.onError = onError
    callbacks.onFinalTranscript = onFinalTranscript
    callbacks.onInterimTranscript = onInterimTranscript
    callbacks.onMicrophoneReady = onMicrophoneReady
    callbacks.onStatusChange = onStatusChange
  })

  useEffect(() => {
    statusRef.current = status
  }, [status])

  // Session resources.
  const finalizedRef = useRef(EMPTY_STRING)
  const interimRef = useRef(EMPTY_STRING)
  const drainTimerIdRef = useRef(null)
  const acceptingResultsRef = useRef(false)
  const micRef = useRef(null)
  const wsRef = useRef(null)

  function clearDrainTimer() {
    if (drainTimerIdRef.current !== null) {
      clearTimeout(drainTimerIdRef.current)
      drainTimerIdRef.current = null
    }
  }

  function resetSessionState() {
    finalizedRef.current = EMPTY_STRING
    interimRef.current = EMPTY_STRING
    setFinalTranscript(EMPTY_STRING)
    setInterimTranscript(EMPTY_STRING)
  }

  function setStatus(nextStatus) {
    if (isMountedRef.current) {
      statusRef.current = nextStatus
      setStatusState(nextStatus)
      callbacksRef.current.onStatusChange(nextStatus)
    }
  }

  function teardownSession() {
    clearDrainTimer()
    acceptingResultsRef.current = false
    wsRef.current?.disconnect()
    micRef.current?.release()
  }

  function failWith(nextErrorCode, closeInfo = null) {
    if (isMountedRef.current && !isSpeechToTextStatusIdle(statusRef.current)) {
      teardownSession()
      setErrorCode(nextErrorCode)
      callbacksRef.current.onError(nextErrorCode, closeInfo)
      setStatus(SpeechToTextStatus.Idle)
    }
  }

  function completeStop() {
    if (isMountedRef.current) {
      teardownSession()
      setStatus(SpeechToTextStatus.Idle)
    }
  }

  function endRecording() {
    if (
      isSpeechToTextStatusReady(statusRef.current) ||
      isSpeechToTextStatusRecording(statusRef.current)
    ) {
      setStatus(SpeechToTextStatus.Stopping)

      micRef.current?.stopCapture()
      wsRef.current?.send(
        JSON.stringify({ type: DeepgramMessageType.CloseStream })
      )

      clearDrainTimer()
      drainTimerIdRef.current = setTimeout(() => {
        if (isSpeechToTextStatusStopping(statusRef.current)) {
          completeStop()
        }
      }, drainTimeoutMs)
    }
  }

  function handleResultsMessage(payload) {
    if (
      acceptingResultsRef.current &&
      isDeepgramMessageTypeResults(payload?.type)
    ) {
      const alt = payload.channel?.alternatives?.[0]

      if (alt) {
        const transcript = (alt.transcript || EMPTY_STRING).trim()
        const isFinal = Boolean(payload.is_final)
        const speechFinal = Boolean(payload.speech_final)

        if (isFinal) {
          const nextFinalized = transcript
            ? [finalizedRef.current, transcript].filter(Boolean).join(' ')
            : finalizedRef.current

          finalizedRef.current = nextFinalized
          interimRef.current = EMPTY_STRING
          setFinalTranscript(nextFinalized)
          setInterimTranscript(EMPTY_STRING)

          if (transcript) {
            callbacksRef.current.onFinalTranscript(nextFinalized, transcript)
          }
        } else {
          interimRef.current = transcript
          setInterimTranscript(transcript)
          callbacksRef.current.onInterimTranscript(transcript)
        }

        if (transcript && isSpeechToTextStatusReady(statusRef.current)) {
          setStatus(SpeechToTextStatus.Recording)
        }

        if (
          isSpeechToTextStatusStopping(statusRef.current) &&
          isFinal &&
          speechFinal
        ) {
          completeStop()
        }
      }
    }
  }

  function handleSocketOpen() {
    if (micRef.current?.startCapture()) {
      acceptingResultsRef.current = true
      setStatus(SpeechToTextStatus.Ready)
    }
  }

  function handleSocketError(code) {
    const outerCode =
      DEEPGRAM_WEB_SOCKET_ERROR_TO_SPEECH_TO_TEXT_ERROR[code] ??
      SpeechToTextError.ServiceError

    failWith(outerCode)
  }

  function handleSocketClose(closeEvent) {
    if (isSpeechToTextStatusStopping(statusRef.current)) {
      completeStop()
    } else if (
      !isWebSocketNormalCloseCode(closeEvent.code) ||
      acceptingResultsRef.current
    ) {
      const innerCode = mapDeepgramCloseToError(
        closeEvent.code,
        closeEvent.reason
      )
      const outerCode =
        DEEPGRAM_WEB_SOCKET_ERROR_TO_SPEECH_TO_TEXT_ERROR[innerCode] ??
        SpeechToTextError.ServiceError

      failWith(outerCode, {
        closeCode: closeEvent.code,
        closeReason: closeEvent.reason,
      })
    }
  }

  function handleMicReady() {
    callbacksRef.current.onMicrophoneReady()
    wsRef.current?.connect(DEEPGRAM_LIVE_URL, liveQuery)
  }

  function handleMicChunk(chunk) {
    wsRef.current?.send(chunk)
  }

  function handleMicError(code) {
    const outerCode =
      MICROPHONE_STREAM_ERROR_TO_SPEECH_TO_TEXT_ERROR[code] ??
      SpeechToTextError.ServiceError

    failWith(outerCode)
  }

  const ws = useDeepgramWebSocket({
    onClose: handleSocketClose,
    onError: handleSocketError,
    onMessage: handleResultsMessage,
    onOpen: handleSocketOpen,
  })

  const mic = useMicrophoneStream({
    chunkMs: recorderChunkMs,
    mimeType,
    onChunk: handleMicChunk,
    onError: handleMicError,
    onReady: handleMicReady,
  })

  // Latest-ref syncs for the WS and mic instances. The sub-hooks return a new
  // object each render; mirroring them into refs lets stable callbacks like
  // teardownSession / handleMicReady reach the current instance without
  // depending on `ws` / `mic` directly — otherwise every internal callback
  // would be recreated each render and the sub-hooks would re-subscribe.
  useEffect(() => {
    wsRef.current = ws
  })

  useEffect(() => {
    micRef.current = mic
  })

  function start() {
    if (isSpeechToTextStatusIdle(statusRef.current)) {
      setErrorCode(null)

      if (!isValueStringType(apiKey) || !apiKey) {
        setErrorCode(SpeechToTextError.ConfigError)
        callbacksRef.current.onError(SpeechToTextError.ConfigError, null)
      } else if (!isMediaRecorderSupported(mimeType)) {
        setErrorCode(SpeechToTextError.RecorderUnsupported)
        callbacksRef.current.onError(
          SpeechToTextError.RecorderUnsupported,
          null
        )
      } else {
        resetSessionState()

        // Probe the Permissions API first so a previously-denied mic surfaces
        // the error without ever entering Connecting state. query() resolves
        // in a microtask and doesn't trigger a system prompt, so the cost is
        // invisible.
        probeMicrophonePermission().then(permissionState => {
          if (
            isMountedRef.current &&
            isSpeechToTextStatusIdle(statusRef.current)
          ) {
            if (isMicrophonePermissionStateDenied(permissionState)) {
              setErrorCode(SpeechToTextError.MicrophonePermissionDenied)
              callbacksRef.current.onError(
                SpeechToTextError.MicrophonePermissionDenied,
                null
              )
            } else {
              setStatus(SpeechToTextStatus.Connecting)
              micRef.current?.acquire()
            }
          }
        })
      }
    }
  }

  function stop() {
    if (
      isSpeechToTextStatusReady(statusRef.current) ||
      isSpeechToTextStatusRecording(statusRef.current)
    ) {
      endRecording()
    }
  }

  function cancel() {
    if (!isSpeechToTextStatusIdle(statusRef.current)) {
      completeStop()
    }
  }

  useEffect(() => {
    isMountedRef.current = true

    return () => {
      isMountedRef.current = false

      if (drainTimerIdRef.current !== null) {
        clearTimeout(drainTimerIdRef.current)
        drainTimerIdRef.current = null
      }
    }
  }, [])

  return {
    cancel,
    errorCode,
    finalTranscript,
    interimTranscript,
    start,
    status,
    stop,
  }
}
